/** Renders the arc-shaped attack visual that tracks attack state (windup/active/recovery) and fades on recovery. */
import Phaser from 'phaser';
import { Component } from './Component';
import { ConfigLoader } from '../systems/ConfigLoader';
import type { AttackState, AttackType } from './CombatComponent';
import type { Position } from '../utils/Types';
import { normalize, degreesToRadians, directionToAngle } from '../utils/Math';
import { CRISP_SCALE } from '../ui/CrispUtil';

const WINDUP_OFFSET = 10;
const ACTIVE_OFFSET = 16;
const WINDUP_RADIUS_SCALE = 0.5;
const WINDUP_ALPHA = 0.3;
const ACTIVE_ALPHA = 0.8;
const RECOVERY_FADE_MS = 100;

/** Max arc radius we need to support (logical pixels). Sized generously to avoid clipping. */
const MAX_ARC_RADIUS = 40;
/** Max line width (logical pixels). */
const MAX_LINE_WIDTH = 6;
/** Canvas size in crisp pixels — enough for the largest arc plus stroke padding. */
const ARC_CANVAS_SIZE = (MAX_ARC_RADIUS + MAX_LINE_WIDTH) * 2 * CRISP_SCALE + 4;

const INV_CRISP = 1 / CRISP_SCALE;
const ARC_TEX_KEY = '_attack-arc';

/** Draws a crisp arc in front of the player during attack phases; dims during windup, brightens on active, tweens out on recovery. */
export class AttackVisualComponent implements Component {
  private scene: Phaser.Scene;
  private ownerSprite: Phaser.GameObjects.Sprite;
  private events: Phaser.Events.EventEmitter;
  private facingDirection: Position;

  private arcCanvas: HTMLCanvasElement;
  private arcCtx: CanvasRenderingContext2D;
  private arcImage: Phaser.GameObjects.Image;
  private currentAttackType: AttackType = 'punch';
  private skillArcActive: boolean = false;

  private currentTween: Phaser.Tweens.Tween | null = null;
  private currentState: AttackState = 'idle';
  private alpha: number = 0;

  constructor(
    scene: Phaser.Scene,
    ownerSprite: Phaser.GameObjects.Sprite,
    events: Phaser.Events.EventEmitter,
    facingDirection: Position
  ) {
    this.scene = scene;
    this.ownerSprite = ownerSprite;
    this.events = events;
    this.facingDirection = facingDirection;

    this.arcCanvas = document.createElement('canvas');
    this.arcCanvas.width = ARC_CANVAS_SIZE;
    this.arcCanvas.height = ARC_CANVAS_SIZE;
    this.arcCtx = this.arcCanvas.getContext('2d')!;

    if (!scene.textures.exists(ARC_TEX_KEY)) {
      scene.textures.addCanvas(ARC_TEX_KEY, this.arcCanvas);
      scene.textures.get(ARC_TEX_KEY).setFilter(Phaser.Textures.FilterMode.LINEAR);
    }

    this.arcImage = scene.add.image(0, 0, ARC_TEX_KEY)
      .setScale(INV_CRISP)
      .setVisible(false);

    this.events.on('attack-state-changed', this.onAttackStateChanged, this);
  }

  private onAttackStateChanged(state: AttackState, attackType: AttackType): void {
    this.currentState = state;

    // Punch and Punchmore use their own effects — skill arc stays inactive for them
    this.skillArcActive = attackType !== 'punch' && attackType !== 'punchmore';
    this.currentAttackType = attackType;

    this.stopTween();

    switch (state) {
      case 'windup':
        this.alpha = WINDUP_ALPHA;
        break;

      case 'active':
        this.alpha = ACTIVE_ALPHA;
        break;

      case 'recovery':
        if (this.arcImage.visible) {
          this.currentTween = this.scene.tweens.add({
            targets: this,
            alpha: 0,
            duration: RECOVERY_FADE_MS,
            onComplete: () => this.clearEffect(),
          });
        }
        break;

      case 'idle':
        this.clearEffect();
        break;
    }
  }

  private clearEffect(): void {
    this.stopTween();
    this.arcImage.setVisible(false);
    this.alpha = 0;
    this.currentState = 'idle';
  }

  private stopTween(): void {
    if (this.currentTween) {
      this.currentTween.stop();
      this.currentTween = null;
    }
  }

  update(_delta: number): void {
    if (this.currentState === 'idle') {
      this.arcImage.setVisible(false);
      return;
    }

    const isWindup = this.currentState === 'windup';
    const offset = isWindup ? WINDUP_OFFSET : ACTIVE_OFFSET;

    const norm = normalize(this.facingDirection.x, this.facingDirection.y);
    const normX = norm.x;
    const normY = norm.y;

    const isHorizontal = Math.abs(normX) > Math.abs(normY);
    const yNudge = isHorizontal ? -12 : 0;
    const xNudge = !isHorizontal ? (normY > 0 ? 8 : -8) : 0;
    const arcX = this.ownerSprite.x + normX * offset + xNudge;
    const arcY = this.ownerSprite.y + normY * offset + yNudge;

    const angle = directionToAngle(normX, normY);
    const depth = this.ownerSprite.y + 1;

    const skillConfig = this.skillArcActive ? ConfigLoader.getAttackVisuals(this.currentAttackType) : undefined;
    if (skillConfig) {
      const radius = isWindup ? skillConfig.arcRadius * WINDUP_RADIUS_SCALE : skillConfig.arcRadius;
      const sweepRad = degreesToRadians(skillConfig.sweepDegrees);
      const S = CRISP_SCALE;
      const center = ARC_CANVAS_SIZE / 2;
      const ctx = this.arcCtx;

      ctx.clearRect(0, 0, ARC_CANVAS_SIZE, ARC_CANVAS_SIZE);
      ctx.strokeStyle = '#ffffff';
      ctx.lineWidth = skillConfig.lineWidth * S;
      ctx.lineCap = 'round';
      ctx.beginPath();
      ctx.arc(center, center, radius * S, angle - sweepRad / 2, angle + sweepRad / 2, false);
      ctx.stroke();
      (this.scene.textures.get(ARC_TEX_KEY) as Phaser.Textures.CanvasTexture).refresh();

      this.arcImage.setPosition(arcX, arcY);
      this.arcImage.setTint(skillConfig.color);
      this.arcImage.setAlpha(this.alpha);
      this.arcImage.setDepth(depth);
      this.arcImage.setVisible(true);
    } else {
      this.arcImage.setVisible(false);
    }
  }

  destroy(): void {
    this.events.off('attack-state-changed', this.onAttackStateChanged, this);
    this.stopTween();
    this.arcImage.destroy();
    if (this.scene.textures.exists(ARC_TEX_KEY)) {
      this.scene.textures.remove(ARC_TEX_KEY);
    }
    this.alpha = 0;
    this.currentState = 'idle';
  }
}
